import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
} from "@material-ui/core";
import ReceiptIcon from "@material-ui/icons/Receipt";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DescriptionIcon from "@material-ui/icons/Description";
import AssignmentIcon from "@material-ui/icons/Assignment";
import PeopleIcon from "@material-ui/icons/People";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import SchoolIcon from "@material-ui/icons/School";
import EventIcon from "@material-ui/icons/Event";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import SecurityIcon from "@material-ui/icons/Security";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import PageTitle from "../../components/PageTitle/PageTitle";
import SubscriptionService from "./Locality/Service/subscriptionService";
import AcademicYearService from "./Locality/Service/academicYearService";

/** HashRouter: query string lives on the route (location.search), not always window.location.search */
function getOrderIdFromLocation(routeSearch) {
  const fromRoute = new URLSearchParams(routeSearch || "").get("orderId");
  if (fromRoute) return fromRoute;
  const fromWindow = new URLSearchParams(window.location.search || "").get("orderId");
  if (fromWindow) return fromWindow;
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  if (q === -1) return "";
  return new URLSearchParams(hash.slice(q + 1)).get("orderId") || "";
}

const palette = {
  paper: "#ffffff",
  border: "#e8eef4",
  title: "#0f3b62",
  label: "#64748b",
  value: "#0f2744",
  divider: "#eef2f6",
};

/** e.g. "2026-27" → "2026-2027" for display */
function formatAcademicYearDisplay(ay) {
  if (ay == null || ay === "") return "—";
  const s = String(ay).trim();
  if (/^\d{4}-\d{4}$/.test(s)) return s;
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (m) return `${m[1]}-20${m[2]}`;
  return s;
}

function formatDateOnly(d) {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString();
  } catch {
    return "—";
  }
}

function getSocietyName(schoolRef) {
  return String(schoolRef?.societyName || "").trim();
}

function renderSocietyInvoiceHtml(schoolRef) {
  const societyName = getSocietyName(schoolRef);
  if (!societyName) return "";
  return `
            <div class="item">
              <div class="label">Society Name</div>
              <div class="val">${String(societyName).replace(/</g, "&lt;")}</div>
            </div>`;
}

function ReceiptInfoItem({ IconComponent, iconBg, iconColor, label, value, hideIcon = false }) {
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      style={{
        gap: 14,
        padding: "14px 4px",
        borderBottom: `1px solid ${palette.divider}`,
        minHeight: 72,
      }}
    >
      {!hideIcon && IconComponent ? (
        <Box
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconComponent style={{ fontSize: 22, color: iconColor }} />
        </Box>
      ) : null}
      <Box flex={1} minWidth={0}>
        <Typography variant="caption" style={{ color: palette.label, fontWeight: 600, display: "block" }}>
          {label}
        </Typography>
        <Typography
          variant="body1"
          style={{
            color: palette.value,
            fontWeight: 800,
            marginTop: 4,
            wordBreak: "break-word",
          }}
        >
          {value ?? "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function ComputerGeneratedFooter() {
  return (
    <Box mt={3} textAlign="center">
      <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8, marginBottom: 8 }}>
        <SecurityIcon style={{ fontSize: 18, color: "#2563eb" }} />
        <Typography variant="caption" style={{ color: "#475569", fontWeight: 600 }}>
          This is a computer generated receipt and invoice. No signature required.
        </Typography>
      </Box>
      <Box
        style={{
          height: 1,
          background: "linear-gradient(90deg, transparent, #cbd5e1 20%, #cbd5e1 80%, transparent)",
          maxWidth: 400,
          margin: "0 auto",
          position: "relative",
        }}
      >
        <Box
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) rotate(45deg)",
            width: 8,
            height: 8,
            background: "#94a3b8",
          }}
        />
      </Box>
    </Box>
  );
}

export default function UpgradePlan() {
  const routerLocation = useLocation();
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [academicYearId, setAcademicYearId] = useState("");
  const [academicYearOptions, setAcademicYearOptions] = useState([]); // [{ id, label }]
  const [studentCount, setStudentCount] = useState("");
  const [amount, setAmount] = useState("");
  const [pricePerStudent, setPricePerStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [error, setError] = useState("");

  const isSchool = String(userDetails?.role || "").toUpperCase() === "SCHOOLE";

  const paymentOrderId = useMemo(
    () => getOrderIdFromLocation(routerLocation.search),
    [routerLocation.search, routerLocation.pathname],
  );

  useEffect(() => {
    if (!isSchool) return;
    const schooleId = userDetails?.schooleId?._id || userDetails?.schooleId || userDetails?.schoolId;
    if (!schooleId) return;
    let alive = true;
    AcademicYearService.getAllAcademicYear(schooleId)
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res) ? res : [];
        const opts = list
          .filter((x) => String(x?.status || "Active") === "Active")
          .map((x) => ({
            id: String(x?._id || ""),
            label: String(x?.acadamicYear || "").trim(),
          }))
          .filter((x) => x.id && x.label);
        opts.sort((a, b) => {
          const ay = parseInt(String(a.label).split("-")[0], 10) || 0;
          const by = parseInt(String(b.label).split("-")[0], 10) || 0;
          return by - ay;
        });
        setAcademicYearOptions(opts);
        if (opts.length > 0 && !opts.some((x) => x.id === String(academicYearId || ""))) {
          setAcademicYearId(opts[0].id);
        }
      })
      .catch(() => {
        if (!alive) return;
        setAcademicYearOptions([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchool]);

  const fetchQuote = async () => {
    setError("");
    try {
      if (!academicYearId) return;
      const res = await SubscriptionService.getUpgradeQuote(academicYearId);
      const data = res?.data || res;
      const sc = data?.studentCount ?? "";
      const pps = data?.pricePerStudent ?? null;
      setStudentCount(String(sc));
      setPricePerStudent(pps != null ? Number(pps) : null);
      setAmount(String(data?.amount ?? ""));
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load upgrade quote.");
    }
  };

  useEffect(() => {
    if (!isSchool) return;
    fetchQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSchool, academicYearId]);

  useEffect(() => {
    if (!isSchool) return;
    if (!paymentOrderId) return;
    SubscriptionService.getReceipt(paymentOrderId)
      .then((res) => {
        const data = res?.data || res;
        setReceipt(data?.payment || null);
      })
      .catch(() => {});
  }, [isSchool, paymentOrderId]);

  // After successful payment, load invoice so both cards match the designed layout.
  useEffect(() => {
    if (!isSchool || !receipt || String(receipt.status || "").toUpperCase() !== "SUCCESS") return;
    if (!paymentOrderId) return;
    let alive = true;
    SubscriptionService.getInvoice(paymentOrderId)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res;
        setInvoice(data?.invoice || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isSchool, receipt, paymentOrderId]);

  // Default behavior: load all successful invoices for the school
  useEffect(() => {
    if (!isSchool) return;
    let alive = true;
    SubscriptionService.listSuccessfulInvoices(50)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res;
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setInvoiceList(rows);
      })
      .catch(() => {
        if (!alive) return;
        setInvoiceList([]);
      });
    return () => {
      alive = false;
    };
  }, [isSchool, paymentOrderId]);

  const handleInvoice = async () => {
    setError("");
    setInvoice(null);
    const orderId = paymentOrderId || getOrderIdFromLocation(routerLocation.search);
    if (!orderId) {
      setError("OrderId missing. Please complete payment first.");
      return;
    }
    setInvoiceLoading(true);
    try {
      const res = await SubscriptionService.getInvoice(orderId);
      const data = res?.data || res;
      setInvoice(data?.invoice || null);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load invoice.");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDownloadInvoice = (inv) => {
    const invoiceToUse = inv || invoice;
    if (!invoiceToUse) {
      setError("Invoice not loaded yet.");
      return;
    }

    // Browser-native download path: open print view → user can "Save as PDF".
    const title = `Invoice - ${invoiceToUse?.orderId || "order"}`;
    const html = `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1"/>
    <title>${String(title).replace(/</g, "&lt;")}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; padding: 24px; font-family: Arial, Helvetica, sans-serif; background: #ffffff; color: #0f2744; }
      .wrap { max-width: 900px; margin: 0 auto; }
      .card { border: 1px solid #e8eef4; border-radius: 16px; padding: 22px 26px; }
      .h { display:flex; align-items:center; justify-content:space-between; gap: 12px; }
      .t { font-size: 20px; font-weight: 900; color: #0f3b62; margin: 0; }
      .brand { margin: 0; line-height: 1.2; }
      .brand1 { font-size: 18px; font-weight: 900; color: #0f3b62; }
      .brand2 { font-size: 13px; font-weight: 800; color: #334155; margin-top: 2px; }
      .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0; margin-top: 12px; }
      .item { padding: 12px 2px; border-bottom: 1px solid #eef2f6; }
      .label { font-size: 11px; color: #64748b; font-weight: 700; }
      .val { margin-top: 6px; font-size: 15px; font-weight: 800; word-break: break-word; }
      .foot { margin-top: 18px; font-size: 12px; color: #475569; text-align: center; font-weight: 600; }
      @media print {
        body { padding: 0; }
        .card { border: none; border-radius: 0; padding: 0; }
      }
      @media (max-width: 720px) {
        .grid { grid-template-columns: 1fr; }
      }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="card">
        <div class="h">
          <div>
            <p class="brand brand1">Montestory.in</p>
            <p class="brand brand2">(Srshta Tech Solutions Pvt Ltd)</p>
          </div>
          <h1 class="t">Invoice</h1>
        </div>
        <div class="grid">
          <div>
            <div class="item">
              <div class="label">School</div>
              <div class="val">${String(invoiceToUse?.schooleId?.schooleName || "—")}</div>
            </div>
            ${renderSocietyInvoiceHtml(invoiceToUse?.schooleId)}
            <div class="item">
              <div class="label">Academic Year</div>
              <div class="val">${String(formatAcademicYearDisplay(invoiceToUse?.academicYear) || "—")}</div>
            </div>
            <div class="item">
              <div class="label">Date</div>
              <div class="val">${String(formatDateOnly(invoiceToUse?.paidAt) || "—")}</div>
            </div>
          </div>
          <div>
            <div class="item">
              <div class="label">Order ID</div>
              <div class="val">${String(invoiceToUse?.orderId || "—")}</div>
            </div>
            <div class="item">
              <div class="label">Students</div>
              <div class="val">${invoiceToUse?.studentCount != null ? String(invoiceToUse.studentCount) : "—"}</div>
            </div>
          </div>
          <div>
            <div class="item">
              <div class="label">Receipt No.</div>
              <div class="val">${String(invoiceToUse?.receiptNumber || "—")}</div>
            </div>
            <div class="item">
              <div class="label">Amount</div>
              <div class="val">${invoiceToUse?.amount != null ? `₹${Number(invoiceToUse.amount).toLocaleString()}` : "—"}</div>
            </div>
          </div>
        </div>
        <div class="foot">This is a computer generated receipt and invoice. No signature required.</div>
      </div>
      <script>window.onload = () => { window.focus(); window.print(); };</script>
    </div>
  </body>
</html>`;

    const w = window.open("", "_blank");
    if (!w) {
      setError("Popup blocked. Please allow popups to download invoice.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handlePay = async () => {
    setLoading(true);
    setError("");
    try {
      const sc = Number(studentCount || 0);
      const res = await SubscriptionService.initiateUpgradePayment({
        academicYearId,
        studentCount: Number.isFinite(sc) && sc > 0 ? sc : undefined,
      });
      const data = res?.data || res;
      const ccavUrl =
        process.env.REACT_APP_CCAV_URL || "https://test.ccavenue.com/transaction/transaction.do?command=initiateTransaction";
      if (!data?.encRequest || !data?.access_code) {
        throw new Error("Missing CCAvenue params from server (encRequest/access_code).");
      }

      const form = document.createElement("form");
      form.method = "POST";
      form.action = ccavUrl;

      const addHidden = (name, value) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      addHidden("encRequest", data.encRequest);
      addHidden("access_code", data.access_code);

      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Payment initiation failed.");
      setLoading(false);
    }
  };

  const receiptSuccess = receipt && String(receipt.status || "").toUpperCase() === "SUCCESS";
  const invoiceDateStr = formatDateOnly(invoice?.paidAt);

  if (!isSchool) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageTitle title="Upgrade to Paid plan" />

      <Card style={{ maxWidth: 900, marginTop: 16, borderRadius: 16, boxShadow: "0 4px 24px rgba(15, 59, 98, 0.08)" }}>
        <CardContent>
          <Box display="flex" flexDirection="column" style={{ gap: 14 }}>
            {academicYearOptions.length > 0 ? (
              <FormControl variant="outlined">
                <InputLabel id="upgrade-academic-year-label">Academic year</InputLabel>
                <Select
                  labelId="upgrade-academic-year-label"
                  value={academicYearId}
                  onChange={(e) => setAcademicYearId(String(e.target.value))}
                  label="Academic year"
                >
                  {academicYearOptions.map((y) => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Academic year"
                value={academicYearId}
                onChange={(e) => setAcademicYearId(e.target.value)}
                variant="outlined"
                placeholder="2026-27"
              />
            )}

            <TextField
              label="Number of students"
              value={studentCount}
              variant="outlined"
              onChange={(e) => {
                const raw = e?.target?.value ?? "";
                const digits = String(raw).replace(/[^\d]/g, "");
                setStudentCount(digits);

                const n = Number(digits || 0);
                const pps = Number(pricePerStudent);
                if (Number.isFinite(n) && n > 0 && Number.isFinite(pps) && pps > 0) {
                  setAmount(String(n * pps));
                }
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            />

            <TextField
              label="Amount to be paid"
              value={amount ? `₹${Number(amount).toLocaleString()}` : ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />

            {error ? (
              <Paper style={{ padding: 10, background: "#fff7ed", border: "1px solid #fed7aa" }}>
                <Typography style={{ color: "#9a3412", fontWeight: 700 }}>{error}</Typography>
              </Paper>
            ) : null}

            <Box display="flex" alignItems="center" style={{ gap: 12 }} flexWrap="wrap">
              <Button variant="outlined" onClick={fetchQuote} disabled={loading} style={{ textTransform: "none" }}>
                Refresh amount
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePay}
                disabled={loading || !amount || Number(amount) <= 0}
                style={{ textTransform: "none", fontWeight: 800 }}
              >
                Click here to pay online
              </Button>

              {receiptSuccess ? (
                <Button
                  variant="contained"
                  onClick={handleInvoice}
                  disabled={invoiceLoading}
                  style={{ textTransform: "none", fontWeight: 800, background: "#111827", color: "white" }}
                >
                  {invoiceLoading ? "Loading invoice..." : "Refresh invoice"}
                </Button>
              ) : null}
            </Box>

          </Box>
        </CardContent>
      </Card>

      {/* If orderId is present in URL, show that invoice. Otherwise show all successful invoices. */}
      {!paymentOrderId && Array.isArray(invoiceList) && invoiceList.length > 0
        ? invoiceList.map((inv) => (
          <Card
            key={inv.orderId}
            style={{
              maxWidth: 900,
              marginTop: 24,
              borderRadius: 16,
              boxShadow: "0 4px 24px rgba(15, 59, 98, 0.08)",
              border: `1px solid ${palette.border}`,
              overflow: "hidden",
              background: palette.paper,
            }}
          >
            <CardContent style={{ padding: "24px 28px 20px" }}>
              {/* Header row - match screenshot: left brand, centered Invoice, right download */}
              <Box
                display="grid"
                style={{
                  gridTemplateColumns: "minmax(260px, 1fr) auto minmax(220px, 1fr)",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <Box display="flex" alignItems="center" style={{ gap: 14 }}>
                  <Box display="flex" flexDirection="column" style={{ lineHeight: 1.15 }}>
                    <Typography style={{ fontWeight: 900, fontSize: "1.15rem", color: palette.title }}>Montestory.in</Typography>
                    <Typography style={{ fontWeight: 800, fontSize: "0.9rem", color: "#334155" }}>
                      (Srshta Tech Solutions Pvt Ltd)
                    </Typography>
                  </Box>
                </Box>

                <Box textAlign="center">
                  <Typography style={{ fontWeight: 900, fontSize: "1.75rem", color: palette.title, letterSpacing: 0.2 }}>
                    Invoice
                  </Typography>
                  <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 6, gap: 10 }}>
                    <Box style={{ width: 74, height: 2, background: "#cbd5e1", borderRadius: 2 }} />
                    <Box display="flex" style={{ gap: 6 }}>
                      <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
                      <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
                      <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
                    </Box>
                    <Box style={{ width: 74, height: 2, background: "#cbd5e1", borderRadius: 2 }} />
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => handleDownloadInvoice(inv)}
                    style={{
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: "#93c5fd",
                      color: "#1d4ed8",
                      background: "#ffffff",
                    }}
                  >
                    Download Invoice
                  </Button>
                </Box>
              </Box>

              <Box style={{ height: 1, background: palette.divider, margin: "18px 0 8px" }} />

              <Grid container spacing={0}>
                <Grid item xs={12} sm={4}>
                  <ReceiptInfoItem
                    IconComponent={AccountBalanceIcon}
                    iconBg="#e0f2fe"
                    iconColor="#0284c7"
                    label="School"
                    value={inv?.schooleId?.schooleName || "—"}
                    hideIcon
                  />
                  {getSocietyName(inv?.schooleId) ? (
                    <ReceiptInfoItem
                      IconComponent={AccountBalanceIcon}
                      iconBg="#e0f2fe"
                      iconColor="#0284c7"
                      label="Society Name"
                      value={getSocietyName(inv?.schooleId)}
                      hideIcon
                    />
                  ) : null}
                  <ReceiptInfoItem
                    IconComponent={SchoolIcon}
                    iconBg="#ede9fe"
                    iconColor="#7c3aed"
                    label="Academic Year"
                    value={formatAcademicYearDisplay(inv.academicYear)}
                    hideIcon
                  />
                  <ReceiptInfoItem
                    IconComponent={EventIcon}
                    iconBg="#fee2e2"
                    iconColor="#dc2626"
                    label="Date"
                    value={formatDateOnly(inv?.paidAt)}
                    hideIcon
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ReceiptInfoItem
                    IconComponent={AssignmentIcon}
                    iconBg="#ffedd5"
                    iconColor="#ea580c"
                    label="Order ID"
                    value={inv.orderId}
                    hideIcon
                  />
                  <ReceiptInfoItem
                    IconComponent={PeopleIcon}
                    iconBg="#dbeafe"
                    iconColor="#2563eb"
                    label="Students"
                    value={inv.studentCount != null ? String(inv.studentCount) : "—"}
                    hideIcon
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ReceiptInfoItem
                    IconComponent={DescriptionIcon}
                    iconBg="#e0f2fe"
                    iconColor="#0284c7"
                    label="Receipt No."
                    value={inv.receiptNumber || "—"}
                    hideIcon
                  />
                  <ReceiptInfoItem
                    IconComponent={AttachMoneyIcon}
                    iconBg="#dcfce7"
                    iconColor="#16a34a"
                    label="Amount"
                    value={inv.amount != null ? `₹${Number(inv.amount).toLocaleString()}` : "—"}
                    hideIcon
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))
        : null}

      {paymentOrderId && invoice ? (
        <Card
          style={{
            maxWidth: 900,
            marginTop: 24,
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(15, 59, 98, 0.08)",
            border: `1px solid ${palette.border}`,
            overflow: "hidden",
            background: palette.paper,
          }}
        >
          <CardContent style={{ padding: "24px 28px 20px" }}>
            {/* Header row - match screenshot: left brand, centered Invoice, right download */}
            <Box
              display="grid"
              style={{
                gridTemplateColumns: "minmax(260px, 1fr) auto minmax(220px, 1fr)",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 14 }}>
                <Box display="flex" flexDirection="column" style={{ lineHeight: 1.15 }}>
                  <Typography style={{ fontWeight: 900, fontSize: "1.15rem", color: palette.title }}>Montestory.in</Typography>
                  <Typography style={{ fontWeight: 800, fontSize: "0.9rem", color: "#334155" }}>
                    (Srshta Tech Solutions Pvt Ltd)
                  </Typography>
                </Box>
              </Box>

              <Box textAlign="center">
                <Typography style={{ fontWeight: 900, fontSize: "1.75rem", color: palette.title, letterSpacing: 0.2 }}>
                  Invoice
                </Typography>
                <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 6, gap: 10 }}>
                  <Box style={{ width: 74, height: 2, background: "#cbd5e1", borderRadius: 2 }} />
                  <Box display="flex" style={{ gap: 6 }}>
                    <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
                    <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
                    <Box style={{ width: 6, height: 6, borderRadius: 6, background: "#2563eb" }} />
                  </Box>
                  <Box style={{ width: 74, height: 2, background: "#cbd5e1", borderRadius: 2 }} />
                </Box>
              </Box>

              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => handleDownloadInvoice(invoice)}
                  style={{
                    textTransform: "none",
                    fontWeight: 800,
                    borderColor: "#93c5fd",
                    color: "#1d4ed8",
                    background: "#ffffff",
                  }}
                >
                  Download Invoice
                </Button>
              </Box>
            </Box>

            <Box style={{ height: 1, background: palette.divider, margin: "18px 0 8px" }} />

            <Grid container spacing={0}>
              <Grid item xs={12} sm={4}>
                <ReceiptInfoItem
                  IconComponent={AccountBalanceIcon}
                  iconBg="#e0f2fe"
                  iconColor="#0284c7"
                  label="School"
                  value={invoice?.schooleId?.schooleName || "—"}
                  hideIcon
                />
                {getSocietyName(invoice?.schooleId) ? (
                  <ReceiptInfoItem
                    IconComponent={AccountBalanceIcon}
                    iconBg="#e0f2fe"
                    iconColor="#0284c7"
                    label="Society Name"
                    value={getSocietyName(invoice?.schooleId)}
                    hideIcon
                  />
                ) : null}
                <ReceiptInfoItem
                  IconComponent={SchoolIcon}
                  iconBg="#ede9fe"
                  iconColor="#7c3aed"
                  label="Academic Year"
                  value={formatAcademicYearDisplay(invoice.academicYear)}
                  hideIcon
                />
                <ReceiptInfoItem
                  IconComponent={EventIcon}
                  iconBg="#fee2e2"
                  iconColor="#dc2626"
                  label="Date"
                  value={invoiceDateStr}
                  hideIcon
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReceiptInfoItem
                  IconComponent={AssignmentIcon}
                  iconBg="#ffedd5"
                  iconColor="#ea580c"
                  label="Order ID"
                  value={invoice.orderId}
                  hideIcon
                />
                <ReceiptInfoItem
                  IconComponent={PeopleIcon}
                  iconBg="#dbeafe"
                  iconColor="#2563eb"
                  label="Students"
                  value={invoice.studentCount != null ? String(invoice.studentCount) : "—"}
                  hideIcon
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReceiptInfoItem
                  IconComponent={DescriptionIcon}
                  iconBg="#e0f2fe"
                  iconColor="#0284c7"
                  label="Receipt No."
                  value={invoice.receiptNumber || "—"}
                  hideIcon
                />
                <ReceiptInfoItem
                  IconComponent={AttachMoneyIcon}
                  iconBg="#dcfce7"
                  iconColor="#16a34a"
                  label="Amount"
                  value={invoice.amount != null ? `₹${Number(invoice.amount).toLocaleString()}` : "—"}
                  hideIcon
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      {invoice ? <ComputerGeneratedFooter /> : null}
    </Box>
  );
}
